import { Alert, Flex, message } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import GlobalMapHeader from "./GlobalMapHeader.tsx"
import GlobalMapToolbar from "./GlobalMapToolbar.tsx"
import GlobalRecordSearchInput from "./GlobalRecordSearchInput.tsx"
import ListTab from "./ListTab.tsx"
import {
  MapLoading,
  MapNoData,
  MapNoResults,
  MapProviderError,
  MapStale
} from "./MapStates.tsx"
import RecentActivityInView from "./RecentActivityInView.tsx"
import { AnalyticsTab } from "./analytics/AnalyticsTab.tsx"
import EntityQuickView from "./details/EntityQuickView.tsx"
import EntityQuickViewEmptyState from "./details/EntityQuickViewEmptyState.tsx"
import { buildFeatureId, parseFeatureId } from "./helpers/feature-id"
import { useGlobalMapFacets } from "./hooks/useGlobalMapFacets.ts"
import {
  FEATURE_PARAM,
  useGlobalMapUrlSync
} from "./hooks/useGlobalMapUrlSync.ts"
import { useMapFeatures } from "./hooks/useMapFeatures.ts"
import { EntityIcon } from "./map/EntityIcon.tsx"
import MapCanvas from "./map/MapCanvas.tsx"
import { type FitBounds, computeFit, shouldAutoFit } from "./map/fit.ts"
import { DEFAULT_LAYERS } from "./redux/global-map.constants.ts"
import {
  clearSelection,
  selectActiveFilters,
  selectActiveLayers,
  selectActiveTab,
  selectAnalyticsState,
  selectFeature,
  selectMapData,
  selectQuickView,
  selectSearchTerm,
  selectSelectedFeatureId,
  selectSelectionSeq,
  setActiveLayers,
  setActiveTab,
  setSearchTerm
} from "./redux/global-map.slice"
import { fetchMapFeatures } from "./redux/global-map.thunks"
import {
  type EntityType,
  type FallbackCenter,
  type LeadQuickView,
  type MapCluster,
  type MapFeaturesResponse,
  type ProjectQuickView
} from "./redux/global-map.types"
import { emitMapEvent } from "./telemetry.ts"

// A client-side filter over the already-fetched response — 2026-09-19: the
// layer toggle now ALSO drives the fetch itself (`entities`, in
// `useMapFeatures.ts`), so in steady state `data` already only contains the
// active layers and this is a no-op. It stays as a defensive second pass for
// the transient window between toggling a layer and the resulting fetch
// resolving — without it, a stale response fetched for the PREVIOUS layer
// selection would flash the just-disabled layer's markers for one frame. A
// cluster carries no per-feature entity type, so it is kept whenever it
// holds at least one record of an active layer.
function filterByActiveLayers(
  data: MapFeaturesResponse,
  activeLayers: EntityType[]
): MapFeaturesResponse {
  if (data.mode === "features") {
    return {
      ...data,
      features: data.features.filter((feature) =>
        activeLayers.includes(feature.entity_type)
      )
    }
  }

  const activeSet = new Set(activeLayers)
  return {
    ...data,
    clusters: data.clusters.filter(
      (cluster) =>
        (activeSet.has("project") && cluster.project_count > 0) ||
        (activeSet.has("lead") && cluster.lead_count > 0)
    )
  }
}

// Task 23 — mirrors `parseFeatureParam` in `useGlobalMapUrlSync.ts` exactly
// (never throws; a malformed/unrecognized `?feature=` value resolves to
// `null`), duplicated here rather than imported because that hook's version
// returns `{id, entityType}` with `id` as the raw namespaced form, while this
// one also needs the *bare* id up front for matching against `quickView.data.id`
// later (see the recenter-once effect below) — `parseFeatureId` throwing on
// an unrecognized prefix is what's actually being guarded against.
function parseDeepLinkFeatureParam(
  raw: string | null
): { featureId: string; entityType: EntityType; bareId: string } | null {
  if (!raw) return null
  try {
    const { entityType, id } = parseFeatureId(raw)
    if (!id) return null
    return { featureId: raw, entityType, bareId: id }
  } catch {
    return null
  }
}

// "View on Map" zooms in close on a single project — well past the 15 that
// `MAX_FIT_ZOOM` clamps a multi-point bounds fit to (that cap exists for a
// tight cluster of *several* points; a single point has no such risk).
const RECENTER_ZOOM = 16

/**
 * The live Map tab: wires the redux-backed feature data, the Google Maps
 * canvas and the project quick view together, and overlays the right
 * interaction state for whatever the current fetch/provider status is. The
 * map is the primary, full-width surface (the mockup's own Map Tab layout)
 * — no persistent results rail beside it; a marker click opens the
 * quick-view card instead, which is the only thing that ever occupies the
 * right side. `GlobalRecordSearchInput` — one search box, no behavior wired
 * yet — sits above the title, replacing the earlier "Search address or
 * place" + "Search matching records" pair.
 */
const MapTab = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // `isUrlSynced` gates `useMapFeatures`/`useGlobalMapFacets`'s own initial
  // fetch — see Finding 2 of the Increment 3 final whole-branch review:
  // without it, both hooks' mount effects fired once against the store's
  // pre-URL-sync (default) filters, then fired again once this hook's own
  // mount-read dispatched the real ones, doubling the heaviest queries on
  // any deep link that carries filter params.
  const { isReady: isUrlSynced, pageSizeFromUrl } = useGlobalMapUrlSync()
  useGlobalMapFacets(isUrlSynced)

  const data = useAppSelector(selectMapData)
  const status = useAppSelector((state) => state.globalMap.status)
  const error = useAppSelector((state) => state.globalMap.error)
  const viewport = useAppSelector((state) => state.globalMap.viewport)
  const selectedFeatureId = useAppSelector(selectSelectedFeatureId)
  const selectionSeq = useAppSelector(selectSelectionSeq)
  const activeLayers = useAppSelector(selectActiveLayers)
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const quickView = useAppSelector(selectQuickView)
  const activeTab = useAppSelector(selectActiveTab)
  const analyticsState = useAppSelector(selectAnalyticsState)

  // Task 23 — record-to-map deep link. `useGlobalMapUrlSync` (Task 22)
  // already reads `?feature=` on mount and dispatches `selectFeature`, which
  // is what makes the quick view open at all — nothing here duplicates that.
  // What's captured here is only the *original* deep-link id, read once from
  // the very first render's search params (a later `?feature=` write, e.g.
  // from clicking a different marker, must never retarget this), so the two
  // effects below can each fire their own one-shot behavior for exactly that
  // id and no other. `parseFeatureId` throwing on a malformed value is
  // caught here, same contract as `parseFeatureParam` in the URL-sync hook.
  const deepLinkFeatureRef = useRef<{
    featureId: string
    entityType: EntityType
    bareId: string
  } | null>(parseDeepLinkFeatureParam(searchParams.get(FEATURE_PARAM)))

  const {
    userPanned,
    handleViewportChange,
    markManualNavigation,
    resetManualNavigation,
    viewAll,
    clearSearch,
    showFeaturesByIds
  } = useMapFeatures(isUrlSynced)

  const [providerErrorCode, setProviderErrorCode] = useState<string | null>(
    null
  )
  const [manualFit, setManualFit] = useState<FallbackCenter | FitBounds | null>(
    null
  )
  const quickViewOriginRef = useRef<HTMLElement | null>(null)

  // The Nearby tab's own "Back" chain (`QuickViewNearby`/`handleSelectNearbyFeature`
  // below) — every entry is the record that was on screen right before a
  // Nearby-row click swapped it out, carrying its own coordinate so Back can
  // recenter the camera exactly the way selecting it did. Reset to empty by
  // any OTHER selection path (a marker, a search result, closing the quick
  // view) — those start a fresh browsing context, not a continuation of a
  // Nearby chain. `initialQuickViewTab` travels alongside it: Back is the
  // only path that ever reopens a record on its own Nearby tab rather than
  // Overview.
  const [nearbyBackStack, setNearbyBackStack] = useState<
    {
      id: string
      entityType: EntityType
      latitude: number
      longitude: number
    }[]
  >([])
  const [initialQuickViewTab, setInitialQuickViewTab] = useState<
    "overview" | "nearby"
  >("overview")
  // Set only by `handleSelectFavorite` below, and never cleared by
  // `handleSelectNearbyFeature` — so a chain that starts from this panel
  // (the favorites list, or the near-radius list `EntityQuickViewEmptyState`
  // shows instead of it while "Look Around a Zip Code" is active — same
  // panel, same click handler, same "return to it" semantics either way)
  // and then browses several Nearby hops deep still lands back on that
  // panel (not just the record it started from) once `nearbyBackStack`
  // itself is empty again. Cleared by every OTHER selection path (a marker,
  // a search result, "Show on Map") and by closing the quick view outright,
  // since each of those starts a fresh browsing context unrelated to this
  // panel.
  const [cameFromFavorites, setCameFromFavorites] = useState(false)

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false,
    []
  )

  // Client-side layer filter over the already-fetched mixed response (see
  // `filterByActiveLayers` above) — memoized so toggling a layer doesn't
  // fight the `fitTo`/overlay memoization below with a fresh object identity
  // on every unrelated re-render.
  const filteredData = useMemo(
    () => (data ? filterByActiveLayers(data, activeLayers) : null),
    [data, activeLayers]
  )

  // "Look Around a Zip Code" — while nearZip/nearRadiusMi are both set, the
  // details column (nothing selected) and the map's own empty overlay both
  // key off this instead of their usual behavior (favorites / MapNoData):
  // the whole point of narrowing to a radius is to see what's actually in
  // it, so that wins over the standing favorites list, and a true zero here
  // means "nothing in this radius" (an everyday, expected outcome), not "the
  // org has no mapped locations at all" (`MapNoData`'s own message) — a
  // real, reported bug otherwise. `filteredData` (not raw `data`) so the
  // list stays in sync with the Lead/Project layer toggle, matching what
  // the map itself is showing at the same moment.
  const nearRadiusPanel = useMemo(
    () =>
      filters.nearZip != null && filters.nearRadiusMi != null
        ? {
            zip: filters.nearZip,
            radiusMi: filters.nearRadiusMi,
            features: filteredData?.features ?? [],
            // The TRUE match count, entities-aware and independent of
            // `mode: "clusters"` collapsing `features` to `[]` above a
            // dense radius — see `EntityQuickViewEmptyState`'s own prop
            // doc comment for why this can't just be `features.length`.
            totalCount:
              (filteredData?.project_total ?? 0) +
              (filteredData?.lead_total ?? 0)
          }
        : null,
    [filters.nearZip, filters.nearRadiusMi, filteredData]
  )

  // `computeFit` builds a fresh object every call, so this has to be memoized
  // on `filteredData`/`userPanned` rather than recomputed inline — otherwise
  // every unrelated re-render (e.g. selecting a project) would hand
  // `MapCanvas` a new `fitTo` identity and re-center the camera out from
  // under the user. Fits to the filtered response so the camera frames only
  // what's actually visible with the current layers active.
  const autoFitTo = useMemo(
    () =>
      shouldAutoFit(null, userPanned)
        ? computeFit(filteredData, filters.nearRadiusMi ?? null)
        : null,
    [filteredData, userPanned, filters.nearRadiusMi]
  )
  const fitTo = manualFit ?? autoFitTo

  // A manual fit (recenter / cluster expand) is a one-shot camera command —
  // `MapCanvas` applies it in its own effect (which, as the child, runs
  // before this one in the same commit), so clearing it back to `null` right
  // after lets `autoFitTo` take over on the next render. `handleClusterClick`/
  // `handleRecenter` mark the camera as user-controlled *before* setting this,
  // so by the time it clears, `autoFitTo` has already collapsed to `null`
  // too — otherwise the stale, still-memoized pre-click `autoFitTo` would
  // apply here instead and snap the camera straight back out.
  useEffect(() => {
    if (!manualFit) return
    setManualFit(null)
  }, [manualFit])

  // `MapCanvas` tears down and rebuilds every marker overlay whenever these
  // change identity (they sit in its overlay-render effect's dependency
  // array) — both must stay referentially stable across re-renders.
  //
  // Dispatches `selectFeature` only — `EntityQuickView` already owns
  // fetching the detail payload itself, in its own effect keyed on the
  // selected id (see `details/EntityQuickView.tsx`), and has done so since
  // before this task (originally as `ProjectQuickView`). Also dispatching
  // `fetchQuickView` from here would just double-fire the same request.
  const handleSelectFeature = useCallback(
    (id: string, entityType: EntityType) => {
      quickViewOriginRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      // A direct marker selection starts a fresh browsing context — any
      // Nearby chain the previous record was mid-browsing no longer applies
      // to this one.
      setNearbyBackStack([])
      setCameFromFavorites(false)
      setInitialQuickViewTab("overview")
      dispatch(selectFeature({ id, entityType }))

      // The camera always ends up centered on whichever record was just
      // chosen — a marker near the edge of the current viewport otherwise
      // leaves its quick view open beside a map that never moved to show
      // it. `id` here is the same namespaced (`p:`/`l:`) id `data.features`
      // itself uses, unlike the bare uuid `handleRecenter` below compares
      // on (that one comes from the quick-view payload instead).
      const feature =
        data?.mode === "features"
          ? data.features.find((item) => item.id === id)
          : undefined
      if (feature) {
        markManualNavigation()
        setManualFit({
          latitude: feature.latitude,
          longitude: feature.longitude,
          zoom: RECENTER_ZOOM
        })
      }
    },
    [dispatch, data, markManualNavigation]
  )

  const handleSelectFromMarker = useCallback(
    (id: string, entityType: EntityType) => {
      emitMapEvent("result_selected", { source: "marker" })
      handleSelectFeature(id, entityType)
    },
    [handleSelectFeature]
  )

  // The Nearby tab's own row click (`QuickViewNearby`) — distinct from
  // `handleSelectFeature` above because a nearby record's own coordinate is
  // NOT reliably found by looking it up in `data.features`: Nearby fetches
  // an independent result set (anchored on the selected record's own
  // location, not the main map's committed query/viewport), so a record it
  // lists may never have been part of `data` at all. The caller already has
  // the coordinate on hand (straight off its own `/features` fetch), so it's
  // taken directly here instead of re-deriving it from `data`.
  const handleSelectNearbyFeature = useCallback(
    (feature: {
      id: string
      entityType: EntityType
      latitude: number
      longitude: number
    }) => {
      quickViewOriginRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null

      // Push whatever record is on screen right now, so Back can return to
      // it — only when it actually has a usable coordinate to recenter on
      // (an unmapped record could never have opened this Nearby tab in the
      // first place, but this stays defensive rather than assuming).
      const current = quickView.data
      if (
        current &&
        current.location.latitude != null &&
        current.location.longitude != null
      ) {
        const currentId = `${current.entity_type === "project" ? "p" : "l"}:${current.id}`
        setNearbyBackStack((stack) => [
          ...stack,
          {
            id: currentId,
            entityType: current.entity_type,
            latitude: current.location.latitude as number,
            longitude: current.location.longitude as number
          }
        ])
      }

      // A forward navigation (clicking a Nearby row) always opens on
      // Overview — only Back (below) reopens on Nearby specifically.
      setInitialQuickViewTab("overview")
      dispatch(
        selectFeature({ id: feature.id, entityType: feature.entityType })
      )
      markManualNavigation()
      setManualFit({
        latitude: feature.latitude,
        longitude: feature.longitude,
        zoom: RECENTER_ZOOM
      })
    },
    [dispatch, markManualNavigation, quickView.data]
  )

  // `EntityQuickViewEmptyState`'s own row click — shared by both of its
  // list modes (favorites, and the near-radius list it shows instead while
  // "Look Around a Zip Code" is active), since selecting a row means the
  // exact same thing either way. Structurally close to
  // `handleSelectNearbyFeature` above (select + recenter, given the
  // coordinate directly rather than looked up in `data`), but semantically
  // a fresh browsing context of its own: it never pushes onto
  // `nearbyBackStack` (there is no "previous record" to return to — neither
  // list row is itself a record), and it clears any leftover Nearby chain
  // from an earlier selection. `cameFromFavorites` is what makes the quick
  // view's Back button close back to this panel.
  const handleSelectFavorite = useCallback(
    (feature: {
      id: string
      entityType: EntityType
      latitude: number
      longitude: number
    }) => {
      quickViewOriginRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      setNearbyBackStack([])
      setCameFromFavorites(true)
      setInitialQuickViewTab("overview")
      dispatch(
        selectFeature({ id: feature.id, entityType: feature.entityType })
      )
      markManualNavigation()
      setManualFit({
        latitude: feature.latitude,
        longitude: feature.longitude,
        zoom: RECENTER_ZOOM
      })
    },
    [dispatch, markManualNavigation]
  )

  const handleBackFromNearby = useCallback(() => {
    if (nearbyBackStack.length === 0) return
    const previous = nearbyBackStack[nearbyBackStack.length - 1]

    quickViewOriginRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    setNearbyBackStack((stack) => stack.slice(0, -1))
    setInitialQuickViewTab("nearby")
    dispatch(
      selectFeature({ id: previous.id, entityType: previous.entityType })
    )
    markManualNavigation()
    setManualFit({
      latitude: previous.latitude,
      longitude: previous.longitude,
      zoom: RECENTER_ZOOM
    })
  }, [nearbyBackStack, dispatch, markManualNavigation])

  const handleClusterClick = useCallback(
    (cluster: MapCluster) => {
      markManualNavigation()
      setManualFit({
        latitude: cluster.latitude,
        longitude: cluster.longitude,
        zoom: cluster.expansion_zoom
      })
    },
    [markManualNavigation]
  )

  const handleMapError = useCallback((code: string) => {
    // A provider failure (e.g. the Google Maps SDK failing to load) is a
    // terminal state until the user presses Retry, unlike a fetch rejection
    // that `useMapFeatures`/`fetchMapFeatures` may recover from on its own.
    emitMapEvent("map_error", { error_code: code, retryable: "false" })
    setProviderErrorCode(code)
  }, [])

  const handleRetryProvider = useCallback(() => {
    setProviderErrorCode(null)
  }, [])

  const handleCloseQuickView = useCallback(() => {
    dispatch(clearSelection())
    setNearbyBackStack([])
    setCameFromFavorites(false)
    setInitialQuickViewTab("overview")
    // Selecting a record recenters the camera to street-level zoom
    // (`handleSelectFeature` above) — and that recenter itself, once it
    // settles, reports a real (narrow, street-level) viewport through
    // `handleViewportChange`, which refetches BOUNDED to that tiny area.
    // `resetManualNavigation()` alone only re-enables auto-fit; it doesn't
    // undo that narrowed `data`, so `computeFit(filteredData)` would just
    // fit to whatever handful of records happened to survive the narrow
    // fetch — not "every other record the current filters still match",
    // which is what closing the quick view is actually supposed to
    // restore. An unbounded refetch (`viewport: null`, the same shape the
    // mount-time fetch already uses) gets the full matching set back
    // first; `autoFitTo` then picks up that wider `data` on its own once
    // it lands, since it's memoized on `[filteredData, userPanned]`.
    dispatch(
      fetchMapFeatures({
        viewport: null,
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
    resetManualNavigation()
  }, [dispatch, filters, searchTerm, activeLayers, resetManualNavigation])

  // The quick view's own Back button — one control, two different meanings
  // depending on how this record was reached: pop the Nearby chain if one
  // is active (`nearbyBackStack`, unchanged from `handleBackFromNearby`'s
  // own prior behavior), else, if this record (or an ancestor in its
  // Nearby chain) was originally reached from the favorites list, close
  // back to it — the favorites panel IS `EntityQuickViewEmptyState`, so
  // closing the quick view already shows it, with no separate "return to
  // favorites" action needed.
  const handleBack = useCallback(() => {
    if (nearbyBackStack.length > 0) {
      handleBackFromNearby()
      return
    }
    if (cameFromFavorites) {
      handleCloseQuickView()
    }
  }, [
    nearbyBackStack,
    cameFromFavorites,
    handleBackFromNearby,
    handleCloseQuickView
  ])

  // Bumped by both "Clear All" and "View All" — the ONLY way to reach
  // `GlobalRecordSearchInput`'s own displayed text, since that box's draft
  // is fully local state, never fed by Redux's `searchTerm` (see its own
  // `resetSignal` prop docstring). Without this, either button correctly
  // reset the MAP's data but left the search box still showing the old
  // query — a real, reported bug.
  const [searchResetSignal, setSearchResetSignal] = useState(0)

  // "View All" (`GlobalMapFilterBar`'s own button, right of Clear All) —
  // closes any open quick view (showing one specific record no longer makes
  // sense once every record is back on screen) and hands the rest to
  // `viewAll()`, which owns the unbounded-refetch-vs-the-filters-effect race
  // this needs — see its own doc comment in `useMapFeatures.ts`.
  const handleViewAll = useCallback(() => {
    dispatch(clearSelection())
    viewAll()
    setSearchResetSignal((signal) => signal + 1)
  }, [dispatch, viewAll])

  const handleClearSearch = useCallback(() => {
    clearSearch()
    setSearchResetSignal((signal) => signal + 1)
  }, [clearSearch])

  // The List tab's "Show on Map" bulk action — closes any open quick view
  // (a stale single-record selection would look unrelated to a fresh,
  // curated multi-record view), switches to the Map tab, and fetches +
  // frames exactly the given ids. `showFeaturesByIds` (the hook) owns the
  // fetch/`activeLayers`-widening/race-avoidance; this handler owns what
  // only `MapTab` can — the camera fit (`manualFit` is local state here,
  // same division of labor `handleRecenter`/`handleClusterClick` already
  // have) and the tab switch itself. `ids` come pre-filtered to
  // Project/Lead by `ListTab` (the only entities `showFeaturesByIds`/
  // `/features` can ever return a marker for).
  const handleShowSelectionOnMap = useCallback(
    async (ids: string[]) => {
      dispatch(clearSelection())
      dispatch(setActiveTab("map"))
      const result = await showFeaturesByIds(ids)
      if (result) {
        markManualNavigation()
        setManualFit(computeFit(result))
      }
    },
    [dispatch, showFeaturesByIds, markManualNavigation]
  )

  // "Recent Activity in View"'s own card click — selects the referenced
  // record's quick view, same "fresh browsing context" reset every other
  // selection path here performs. Deliberately does NOT recenter the camera
  // (unlike `handleSelectFeature`/`handleSelectSearchResult`): the event
  // payload carries no coordinate at all (`analytics.py`'s own recent-
  // activity computation never resolves one — see its docstring), and the
  // referenced record may not even be in the currently loaded `data.features`
  // to look one up from. `EntityQuickView` still opens correctly either way,
  // since it fetches its own detail payload independently by id.
  const handleSelectRecentActivityEvent = useCallback(
    (event: { entityType: EntityType; recordId: string }) => {
      quickViewOriginRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      setNearbyBackStack([])
      setCameFromFavorites(false)
      setInitialQuickViewTab("overview")
      dispatch(
        selectFeature({
          id: buildFeatureId(event.entityType, event.recordId),
          entityType: event.entityType
        })
      )
    },
    [dispatch]
  )

  // "View All" on the same card — same destination the Analytics tab's own
  // Recent Activity card sends "View All" to (the List tab, already
  // synchronized to the same shared filters this card reads).
  const handleViewAllRecentActivity = useCallback(() => {
    dispatch(setActiveTab("list"))
  }, [dispatch])

  // `EntityQuickView` calls this with the quick-view payload's own bare id
  // (its `onOpenProject`/`projectId` prop names are unchanged deliberately —
  // see Task 20) — it carries no entity type of its own, so the currently
  // selected feature's namespaced id (`p:`/`l:`) is the source of truth for
  // which route to open. `RoutesPaths.lead.view` is the same route a leads
  // list row opens with (`Leads/LeadsActivePage/components/leads-list-table/
  // columns.tsx`), confirmed as the real lead-detail route rather than
  // guessed.
  const handleOpenFeature = useCallback(
    (id: string) => {
      if (!selectedFeatureId) return
      const { entityType } = parseFeatureId(selectedFeatureId)
      if (entityType === "lead") {
        navigate(RoutesPaths.lead.view(id))
      } else {
        navigate(RoutesPaths.project.view(id).overview)
      }
    },
    [navigate, selectedFeatureId]
  )

  const handleRecenter = useCallback(
    (id: string) => {
      // `id` comes from the quick-view detail payload, which serializes a
      // bare UUID (it isn't a feature) — while `data.features` carries the
      // `p:`-namespaced ids the map/results-list select by. Compare on the
      // same namespace via `parseFeatureId` rather than the raw feature id.
      const feature =
        data?.mode === "features"
          ? data.features.find((item) => parseFeatureId(item.id).id === id)
          : undefined
      if (!feature) return
      markManualNavigation()
      setManualFit({
        latitude: feature.latitude,
        longitude: feature.longitude,
        zoom: RECENTER_ZOOM
      })
    },
    [data, markManualNavigation]
  )

  // "Search address or place" — the sibling of `handleRecenter` above for a
  // place that has no feature backing it (no id, no marker, possibly not
  // even a record in this system at all — it's an arbitrary Google-resolved
  // location). Reuses the same manual-fit pattern `handleRecenter` and
  // `handleClusterClick` already established.
  const handleRecenterToCoordinates = useCallback(
    (lat: number, lng: number) => {
      markManualNavigation()
      setManualFit({ latitude: lat, longitude: lng, zoom: RECENTER_ZOOM })
    },
    [markManualNavigation]
  )

  // A search-panel selection (`GlobalRecordSearchInput`'s own
  // `onSelectResult`) — distinct from `handleSelectFeature` above (which
  // looks the clicked marker up in the already-fetched `data.features`) since
  // a search result may not be in `data` at all: search is unbounded by
  // viewport AND by the active layer toggle, so the record might currently
  // be off-screen, or its whole entity type might be toggled off. The result
  // already carries its own coordinates (`ProjectQuickView`/`LeadQuickView`'s
  // `location`, the same shape `getProjectQuickView`/`getLeadQuickView`
  // return) — no lookup needed, and no second detail fetch either, since
  // `EntityQuickView` still independently fetches by id once `selectFeature`
  // sets `selectedFeatureId` (this only saves the marker-lookup step, not
  // that fetch).
  // A real, reproduced bug: when the selected search result's entity type
  // isn't already an active layer, `setActiveLayers` below is also what
  // `useMapFeatures`'s own "layers changed" effect watches to re-engage
  // `autoFitTo` (`setUserPanned(false)` — see that effect's own comment: a
  // deliberate layer re-scope is meant to re-fit the camera to the new
  // result set). That effect runs in the SAME commit's effect phase as this
  // handler's dispatches, but AFTER them — so calling
  // `handleRecenterToCoordinates` (which calls `markManualNavigation`)
  // synchronously here, in the handler itself, loses the race: the "layers
  // changed" effect's `setUserPanned(false)` fires after, silently
  // re-enabling `autoFitTo`, which then snaps the camera to fit the WHOLE
  // (now-widened, still-stale) dataset instead of the selected record —
  // exactly the "shows on the map but not at the right size, no details
  // panel in view" symptom reported live. Deferring the recenter itself to
  // its own effect (below), which is declared AFTER `useMapFeatures()` is
  // called and therefore always runs AFTER that hook's internal effects for
  // the same commit, makes `markManualNavigation` reliably win instead.
  const pendingSearchRecenterRef = useRef<{
    latitude: number
    longitude: number
  } | null>(null)
  const [searchRecenterRequestId, setSearchRecenterRequestId] = useState(0)

  const handleSelectSearchResult = useCallback(
    (result: ProjectQuickView | LeadQuickView) => {
      quickViewOriginRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      // A search selection starts a fresh browsing context, same reasoning
      // as `handleSelectFeature`'s own reset.
      setNearbyBackStack([])
      setInitialQuickViewTab("overview")

      // Widened, never replaced — the same "never drop the caller's existing
      // set" rule the `?feature=` deep link already established for exactly
      // this reason (Increment 3, Finding 1).
      if (!activeLayers.includes(result.entity_type)) {
        dispatch(setActiveLayers([...activeLayers, result.entity_type]))
      }

      dispatch(
        selectFeature({
          id: buildFeatureId(result.entity_type, result.id),
          entityType: result.entity_type
        })
      )

      const { latitude, longitude } = result.location
      // No coordinates to pan to (an unmapped/low-confidence record) — the
      // quick view still opens via `selectFeature` above; no fallback camera
      // movement is invented, matching `handleRecenterToCoordinates`'s own
      // deep-link precedent for the identical case.
      if (latitude !== null && longitude !== null) {
        pendingSearchRecenterRef.current = { latitude, longitude }
        setSearchRecenterRequestId((id) => id + 1)
      }
    },
    [dispatch, activeLayers]
  )

  // Applies the recenter a search selection queued above — see that
  // handler's own comment for why this can't just happen inline. Fires once
  // per selection (`searchRecenterRequestId` is bumped unconditionally, so
  // this reruns even when the entity type needed no layer widening at all,
  // keeping the behavior identical either way).
  useEffect(() => {
    const pending = pendingSearchRecenterRef.current
    if (!pending) return
    pendingSearchRecenterRef.current = null
    markManualNavigation()
    setManualFit({
      latitude: pending.latitude,
      longitude: pending.longitude,
      zoom: RECENTER_ZOOM
    })
    // Fires strictly on `searchRecenterRequestId`, which is bumped only when
    // a new target is already sitting in `pendingSearchRecenterRef`;
    // `markManualNavigation` is stable (see its own definition).
  }, [searchRecenterRequestId, markManualNavigation])

  // "View all results" (Enter, or the search panel's own trailing row) —
  // this app has no built List tab yet (`GlobalMapToolbar`'s Map/List/
  // Analytics tabs: List is a disabled "Coming soon" stub), so there is no
  // paginated results destination to route to. Committing the query into
  // `searchTerm` narrows the MAP itself to the full matching set instead,
  // reusing the `search` param plumbing `useMapFeatures`'s own debounced
  // search effect already wires end-to-end — the closest available
  // approximation of "show me everything that matched" until a real List
  // view exists. Flagged here, not silently done, so this limitation stays
  // visible in the code rather than only in a conversation.
  const handleViewAllSearchResults = useCallback(
    (query: string) => {
      dispatch(setSearchTerm(query))
      message.info(t("globalMap.recordSearch.viewAllAppliedToMap"))
    },
    [dispatch, t]
  )

  // "Focus" — recenters/zooms the map on the browser's own geolocated
  // position, reusing the same `handleRecenterToCoordinates` pan every other
  // "go to this point" action on this page already uses. A denied permission
  // or an environment with no geolocation support surfaces as a message
  // rather than failing silently on a button the user just clicked.
  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      void message.error(t("globalMap.locateMe.unsupported"))
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        handleRecenterToCoordinates(coords.latitude, coords.longitude),
      () => void message.error(t("globalMap.locateMe.denied"))
    )
  }, [handleRecenterToCoordinates, t])

  // Task 23, gap 1 (Previous/Next needing the deep-linked record's own
  // entity type active as a layer) used to be handled by a separate effect
  // here, dispatching its own `setActiveLayers` after `useGlobalMapUrlSync`'s
  // mount read. The final whole-branch review (Finding 1) found that a
  // second, later dispatch is exactly what broke that hook's
  // `pendingLayersOverride` write guard — it can only converge on ONE value,
  // dispatched once. The widening now happens inside `useGlobalMapUrlSync`
  // itself, as part of its own single mount-time read (it already owns the
  // `?feature=` parsing this depended on), so there is no second dispatch
  // left to go missing.

  const hasHandledDeepLinkRecenterRef = useRef(false)

  // Task 23, gap 2 — a record reached via `?feature=` on a fresh load has no
  // marker click of its own to drive `handleSelectFeature`'s recenter above,
  // so this recenters the camera once the deep-linked record's own
  // quick-view payload has loaded, reusing the same
  // `handleRecenterToCoordinates` (Task 20) `onRecenter`/`onAddressSelect`
  // already call. Guarded to fire at most once, and only for the exact id
  // that was in `?feature=` at mount — closing this quick view and opening a
  // different record afterward must never trigger a second recenter, which
  // `hasHandledDeepLinkRecenterRef` (never reset once set) and the
  // `selectedFeatureId !== deepLink.featureId` early-return both enforce.
  useEffect(() => {
    const deepLink = deepLinkFeatureRef.current
    if (!deepLink || hasHandledDeepLinkRecenterRef.current) return
    if (selectedFeatureId !== deepLink.featureId) return

    // `quickView.data.id` is the bare uuid the detail payload serializes
    // (same convention `handleRecenter` above already relies on) — not yet
    // this specific record's payload (still loading, or belongs to a
    // selection `selectFeature` has since reset via its own `quickView`
    // clear) until it matches.
    if (quickView.data?.id !== deepLink.bareId) return

    hasHandledDeepLinkRecenterRef.current = true

    const { latitude, longitude } = quickView.data.location
    // A low-confidence/unmapped record has no coordinates to pan to — the
    // quick view still opened normally above; no fallback is invented here,
    // same principle Task 20 established for the address-search fallback.
    if (latitude === null || longitude === null) return

    handleRecenterToCoordinates(latitude, longitude)
  }, [selectedFeatureId, quickView, handleRecenterToCoordinates])

  const handleRefresh = useCallback(() => {
    dispatch(
      fetchMapFeatures({
        viewport,
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
  }, [dispatch, viewport, filters, searchTerm, activeLayers])

  // `filteredIsEmpty` (below) can be true either because the server's own
  // `entities`-scoped fetch genuinely found nothing for the active layer(s),
  // or — the zero-active-layers edge case `toggleLayer` deliberately allows
  // — because `fetchMapFeatures`'s `condition` refused to fire at all, in
  // which case `data`/`data.total` are just whatever the last real fetch
  // left behind and `filterByActiveLayers` (above) is doing all the work of
  // reducing it to nothing. `handleResetLayers` recovers from both the same
  // way: turning both layers back on triggers a real, current fetch.
  const handleResetLayers = useCallback(() => {
    dispatch(setActiveLayers(DEFAULT_LAYERS))
  }, [dispatch])

  const filteredIsEmpty = filteredData
    ? filteredData.mode === "features"
      ? filteredData.features.length === 0
      : filteredData.clusters.length === 0
    : false
  const showProviderError = providerErrorCode === "map_provider_unavailable"
  const fetchErrorRetryable = status === "error" && Boolean(error?.retryable)
  const fetchErrorFatal =
    status === "error" && error !== null && !error.retryable

  return (
    <Flex vertical gap={12} className="min-h-0 flex-1">
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        data-testid="global-map-total-announcement"
      >
        {data ? t("globalMap.resultsAnnouncement", { count: data.total }) : ""}
      </span>

      {fetchErrorRetryable && <MapStale onRefresh={handleRefresh} />}
      {fetchErrorFatal && (
        <Alert
          type="error"
          showIcon
          message={t("globalMap.states.fetchError.title")}
        />
      )}

      {/* One search box above the title, replacing the earlier "Search
          address or place" + "Search matching records" pair — see
          `GlobalRecordSearchInput`'s own docstring for the full search
          contract. `handleViewAllSearchResults`'s own comment explains why
          "View all" narrows the map rather than opening a List tab. Renders
          in this exact same place for both the Map and List tabs — an
          earlier version moved it into `GlobalMapFilterBar`'s own List-tab
          picker row instead, which was reverted per explicit product
          direction (it crowded that row and pushed Clear All onto its own
          line). */}
      <GlobalRecordSearchInput
        onSelectResult={handleSelectSearchResult}
        onViewAllResults={handleViewAllSearchResults}
        resetSignal={searchResetSignal}
      />

      {/* Title/subtitle, plus the per-entity KPI totals (tech spec) — these
          read `data.project_total`/`data.lead_total`/etc. directly (never
          `filteredData`), which is safe because `useMapFeatures` now fetches
          with the current `activeLayers` as `entities` (2026-09-19): the
          server itself zeroes a toggled-off layer's total, so the tiles
          agree with the map without needing their own client-side override.
          See `GlobalMapHeader`'s own docstring for the tile styling
          rationale. */}
      <GlobalMapHeader
        projectTotal={data?.project_total}
        leadTotal={data?.lead_total}
        subcontractorTotal={data?.subcontractor_total}
        appointmentTotal={data?.appointment_total}
        // Analytics-tab-only trend arrows (`GlobalMapHeader`'s own doc
        // comment) — reads the plain Redux selector, not `useAnalytics()`
        // itself, so Map/List never trigger an Analytics fetch just by
        // rendering this shared header; `AnalyticsTab` (mounted only while
        // `activeTab === "analytics"`) is what actually populates this data.
        trends={
          activeTab === "analytics" && analyticsState.data
            ? {
                leads: analyticsState.data.totals.leads,
                projects: analyticsState.data.totals.projects,
                subcontractors: analyticsState.data.totals.subcontractors,
                appointments: analyticsState.data.totals.appointments
              }
            : undefined
        }
      />

      {/* Map/List/Analytics tabs + the filter bar, one combined toolbar row
          — see `GlobalMapToolbar`'s own docstring and `handleViewAll`
          above for why this can't be `resetManualNavigation` directly.
          `handleClearSearch` wraps `clearSearch` only to also bump
          `searchResetSignal` — see that state's own comment. */}
      <GlobalMapToolbar
        onViewAll={handleViewAll}
        onClearSearch={handleClearSearch}
      />

      {/* Two-column content grid — `minmax(0, 1fr) 360px`, matching the
          reference exactly (`Target Implementation.png`) at desktop widths
          (>= 1280px, the "desktop breakpoint" for this page specifically —
          this app's own named scale, in `src/constants/breakpoint.ts`, has
          no stop at exactly 1280, so this uses an explicit `min-[1280px]:`
          variant rather than either inventing a mismatched named breakpoint
          or drifting off the number actually specified). CSS Grid rather
          than a `Flex` row specifically so both columns start at the same
          vertical position and the right column stretches to the left
          column's full rendered height (map + Recent Activity)
          automatically, with no manual height math — grid's own default
          `align-items: stretch` does this for free. The right column is
          always present (never conditionally rendered only while a record
          is selected) — the reference never lets the map take the full row.
          Below 1280px there is no room left for a real 360px side column
          without squeezing the map into an unusable sliver or forcing
          horizontal scroll (explicitly disallowed), so it collapses to a
          single column and the two grid items simply stack in DOM order —
          map+Recent Activity first, details panel second, which is already
          the right order and needs no reordering. This preserves the
          "approved" >= 1280px appearance byte-for-byte (`min-[1280px]:` only
          adds the two-column rule on top of the single-column default, it
          never overrides it below that width). */}
      {activeTab === "list" ? (
        <ListTab
          pageSizeFromUrl={pageSizeFromUrl}
          isUrlSynced={isUrlSynced}
          onShowOnMap={handleShowSelectionOnMap}
        />
      ) : activeTab === "analytics" ? (
        <AnalyticsTab />
      ) : (
        <div
          className="grid flex-1 grid-cols-1 gap-3 min-[1280px]:grid-cols-[minmax(0,1fr)_360px]"
          data-testid="global-map-row"
        >
          <div className="flex min-w-0 flex-col gap-3">
            <div className="relative h-[620px] min-w-0 overflow-hidden rounded-lg border border-gray-200">
              {showProviderError ? (
                <MapProviderError onRetry={handleRetryProvider} />
              ) : (
                <MapCanvas
                  data={filteredData}
                  fitTo={fitTo}
                  selectedId={selectedFeatureId}
                  reducedMotion={reducedMotion}
                  onSelectFeature={handleSelectFromMarker}
                  onClusterClick={handleClusterClick}
                  onViewportChange={handleViewportChange}
                  onError={handleMapError}
                  onLocateMe={handleLocateMe}
                  locateMeLabel={t("globalMap.locateMe.label")}
                  fullscreenLabel={t("globalMap.mapControls.fullscreen")}
                  changeMapStyleLabel={t(
                    "globalMap.mapControls.changeMapStyle"
                  )}
                  mapTypeLabel={t("globalMap.mapControls.mapType")}
                  satelliteTypeLabel={t("globalMap.mapControls.satelliteType")}
                  nearCenter={data?.near_center ?? null}
                  nearRadiusMi={filters.nearRadiusMi ?? null}
                />
              )}

              {/* Entity legend, matching the mockup's own bottom-of-map legend
            row — Lead/Project only (the two entity types this app actually
            renders; the mockup's Workforce/Subcontractor/At-Risk-SLA entries
            have no backing data model here yet, see the KPI-tile comment
            above for the same reasoning). Reuses `EntityIcon` — the same
            Leads/Projects icon+color pair the KPI row and Nearby breakdown
            already use, per explicit product direction to move every
            project/lead glyph onto this ONE existing visual language.
            Positioned above Google's own native scale control (bottom of
            the map, `scaleControl: true` on `MapCanvas`) rather than at the
            very bottom edge, to avoid the two fighting for the same corner —
            exact pixel placement here still wants a real-browser check
            (jsdom can't see layout). */}
              {!showProviderError && (
                <div
                  className="pointer-events-none absolute bottom-10 left-4 z-10 flex items-center gap-3 rounded-full bg-white/95 px-3 py-1.5 text-xs text-slate-600 shadow"
                  data-testid="global-map-legend"
                >
                  <span className="flex items-center gap-1">
                    <EntityIcon entityType="lead" className="size-4" />
                    {t("globalMap.entityLabel.lead")}
                  </span>
                  <span className="flex items-center gap-1">
                    <EntityIcon entityType="project" className="size-4" />
                    {t("globalMap.entityLabel.project")}
                  </span>
                </div>
              )}

              {/* Loading now renders centered *inside* this fixed-height map
            rectangle, matching every other map state (`MapNoData`/
            `MapNoResults`/`MapProviderError`) instead of its own top-level
            block above the search/title/filters — that block used to
            reserve a real 220px region above the whole page on every
            fetch (including a refetch from a filter change, not just first
            load), which is exactly the "no large blank/loading area above
            the map" this page must not have. The rectangle's own height
            never changes with `status`, so nothing above or below it
            moves. */}
              {!showProviderError && status === "loading" && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70">
                  <MapLoading />
                </div>
              )}

              {!showProviderError &&
                data &&
                data.total === 0 &&
                !nearRadiusPanel && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="pointer-events-auto rounded-lg bg-white/95 p-4 shadow">
                      <MapNoData />
                    </div>
                  </div>
                )}

              {!showProviderError &&
                data &&
                data.total > 0 &&
                filteredIsEmpty && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="pointer-events-auto rounded-lg bg-white/95 p-4 shadow">
                      <MapNoResults onReset={handleResetLayers} />
                    </div>
                  </div>
                )}
            </div>

            <RecentActivityInView
              onSelectEvent={handleSelectRecentActivityEvent}
              onViewAll={handleViewAllRecentActivity}
            />
          </div>

          {/* Docked beside the map as a real grid column, not a floating/modal
          popup over it — matching the reference's own layout, where the
          selected record's card is a permanent column (part of the same
          row), never an overlay with a backdrop. Always present now, even
          with nothing selected (`EntityQuickViewEmptyState`) — the
          reference never lets the map reclaim this column's width. */}
          <div className="min-w-0" data-testid="global-map-quickview-panel">
            {selectedFeatureId ? (
              <EntityQuickView
                // Forces a full remount (and therefore a fresh `fetchQuickView`
                // call) on every selection event, including a reselect of the
                // SAME record — see `selectFeature`'s own comment in
                // `global-map.slice.ts` for the bug this fixes: without it,
                // clicking a marker that's already the open selection left
                // `quickView.data` reset to `null` with nothing left to
                // re-fetch it, blanking the panel.
                key={selectionSeq}
                projectId={selectedFeatureId}
                onClose={handleCloseQuickView}
                onOpenProject={handleOpenFeature}
                onRecenter={handleRecenter}
                onSelectNearbyFeature={handleSelectNearbyFeature}
                initialTab={initialQuickViewTab}
                canGoBack={nearbyBackStack.length > 0 || cameFromFavorites}
                onBack={handleBack}
                originRef={quickViewOriginRef as React.RefObject<HTMLElement>}
              />
            ) : (
              <EntityQuickViewEmptyState
                onSelectFavorite={handleSelectFavorite}
                nearRadius={nearRadiusPanel}
              />
            )}
          </div>
        </div>
      )}
    </Flex>
  )
}

export default MapTab
